import {startTransition, useCallback, useLayoutEffect, useState} from 'react';
import type {EmblaCarouselType} from 'embla-carousel';

import {usePrefersReducedMotion} from '@/hooks/usePrefersReducedMotion';

type UseDotButtonType = {
	selectedIndex: number;
	scrollSnaps: number[];
	onDotButtonClick: (index: number) => void;
};

export const useDotButton = (
	emblaApi: EmblaCarouselType | undefined,
): UseDotButtonType => {
	const prefersReducedMotion = usePrefersReducedMotion();
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [scrollSnaps, setScrollSnaps] = useState<number[]>(() =>
		emblaApi ? emblaApi.scrollSnapList() : [],
	);

	const onDotButtonClick = useCallback(
		(index: number) => {
			if (!emblaApi) {
				return;
			}
			emblaApi.scrollTo(index, prefersReducedMotion);
		},
		[emblaApi, prefersReducedMotion],
	);

	const onInit = useCallback((emblaApi: EmblaCarouselType) => {
		setScrollSnaps(emblaApi.scrollSnapList());
	}, []);

	const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
		setSelectedIndex(emblaApi.selectedScrollSnap());
	}, []);

	useLayoutEffect(() => {
		if (!emblaApi) {
			return;
		}

		startTransition(() => {
			onInit(emblaApi);
			onSelect(emblaApi);
		});
		emblaApi
			.on('reInit', onInit)
			.on('reInit', onSelect)
			.on('select', onSelect);

		return () => {
			emblaApi
				.off('reInit', onInit)
				.off('reInit', onSelect)
				.off('select', onSelect);
		};
	}, [emblaApi, onInit, onSelect]);

	return {
		selectedIndex,
		scrollSnaps,
		onDotButtonClick,
	};
};
